import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { Typography } from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import useResources from './hooks/useResources';
import { useGetEquipmentInfo } from '../hooks/useGetEquipmentInfo';
import {
  getCalendarEvents,
  getResourcesTimeoffsAndHolidays,
} from 'store/schedule/schedulePageOperation';
import { WarningToolTip } from './WarningToolTip';
import { useDateSettingsFormat } from 'common/useDateSettingsFormat';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import { useResourcesStyles } from './styles';

export const RESOURCE_TYPE = {
  equipment: 'equipment',
  people: 'people',
};

/**
 * Port of the legacy ResourcesList for ScheduleV6.
 *
 * Logic is mostly reused, but:
 * - styles come from useResourcesStyles (JSS hook),
 * - naming and structure are slightly cleaned up for readability.
 */
const ResourcesList = ({ searchValue, isAdminMobile }) => {
  const classes = useResourcesStyles();
  const { dateFormatWithTime, dateFormat } = useDateSettingsFormat();
  const containerRef = useRef(null);
  const dispatch = useDispatch();

  const {
    selectedDate: date,
    availability: scheduleAvailability,
    showAllLocations,
    scheduleDayView,
    scheduleView,
  } = useSelector((state) => state.schedule || {});
  const isGridView = scheduleDayView === 'grid';
  const locations = useSelector((state) => state.personalProfile.activeLocations);
  const currentUser = useSelector((state) => state.personalProfile.user);

  const {
    colorGroups: allColorGroups,
    equipmentOrderInColorGroups: allEquipmentOrderInColorGroups,
  } = useSelector((store) => store.equipmentScheduleOrder);

  const { userList, equipmentList, canDragResources } = useResources();
  const [visibleUsers, setVisibleUsers] = useState(userList);
  const [visibleEquipment, setVisibleEquipment] = useState(equipmentList);
  const [visibleLocations, setVisibleLocations] = useState([]);

  const handleHolidayCreation = useCallback(
    () => dispatch(getCalendarEvents(date)),
    [dispatch, date]
  );

  useEffect(() => {
    setVisibleLocations(locations.map(({ _id }) => _id));
  }, [locations]);

  useEffect(() => {
    dispatch(
      getResourcesTimeoffsAndHolidays({
        date,
        showAllLocations,
        isGridView,
        scheduleView,
      })
    );
  }, [dispatch, date, showAllLocations, isGridView, scheduleView]);

  useEffect(() => {
    const trimmed = searchValue.trim();
    if (!trimmed) {
      setVisibleUsers(userList);
      return;
    }
    setVisibleUsers(
      userList.filter((user) =>
        user.username.toLowerCase().includes(trimmed.toLowerCase())
      )
    );
  }, [userList, searchValue]);

  useEffect(() => {
    const trimmed = searchValue.trim();
    if (!trimmed) {
      setVisibleEquipment(equipmentList);
      return;
    }
    setVisibleEquipment(
      equipmentList.filter((item) =>
        item.name.toLowerCase().includes(trimmed.toLowerCase()) ||
      `${item.number}`.toLowerCase().includes(trimmed.toLowerCase())
      )
    );
  }, [equipmentList, searchValue]);

  const dataReady = useMemo(
    () => Boolean(visibleUsers && visibleEquipment),
    [visibleUsers, visibleEquipment]
  );

  const { getEquipmentInfo } = useGetEquipmentInfo(visibleEquipment);

  const setDragElementData = useCallback(
    (resourceId, resourceType) => (e) => {
      e.dataTransfer.setData('resourceId', resourceId);
      e.dataTransfer.setData('resourceType', resourceType);
    },
    []
  );

  const [colorGroupsMap, equipmentOrderMap] = useMemo(() => {
    const colorGroupsMap = {};
    Object.entries(allColorGroups).forEach(([tenantLocationId, colorGroups]) => {
      colorGroupsMap[tenantLocationId] =
        colorGroups?.reduce((obj, group) => {
          // eslint-disable-next-line no-param-reassign
          obj[group.color] = group;
          return obj;
        }, {}) || {};
    });

    const equipmentOrderMap = {};
    Object.entries(allEquipmentOrderInColorGroups).forEach(
      ([tenantLocationId, equipmentOrder]) => {
        equipmentOrderMap[tenantLocationId] =
          equipmentOrder?.reduce((obj, group) => {
            // eslint-disable-next-line no-param-reassign
            obj[group.equipmentId] = group;
            return obj;
          }, {}) || {};
      }
    );

    return [colorGroupsMap, equipmentOrderMap];
  }, [allColorGroups, allEquipmentOrderInColorGroups]);

  const locationsResources = useMemo(
    () =>
      locations
        .filter?.(
          (location) =>
            showAllLocations || location._id === currentUser.tenantLocationId
        )
        .map((location) => ({
          location,
          onLocationClick: () => {
            if (visibleLocations?.includes(location._id)) {
              setVisibleLocations(
                visibleLocations.filter((locationId) => locationId !== location._id)
              );
            } else {
              setVisibleLocations([...visibleLocations, location._id]);
            }
          },
          isLocationVisible: visibleLocations?.includes(location._id),
          resources: [
            {
              title: 'People',
              data: visibleUsers?.filter((user) =>
                user.assignedToLocations?.includes(location._id)
              ),
              unavailableClass: 'unavailableUser',
              resourceType: 'workers',
              timeoffs: scheduleAvailability.timeoffs,
              holidays: scheduleAvailability.holidays,
              type: RESOURCE_TYPE.people,
              Template: ({ item }) => (
                <div
                  className={classnames(
                    classes.flexBox,
                    item.isUsed && classes.usedResource,
                    canDragResources && classes.cursorPointer
                  )}
                >
                  <Typography
                    variant="body1"
                    color="textPrimary"
                    className={classnames(
                      classes.username,
                      classes[item.profile.shifts.timeOfDay.toLowerCase()]
                    )}
                  >
                    {item.username} ({item.profile.shifts.weekDays})
                  </Typography>
                </div>
              ),
            },
            {
              title: 'Equipment',
              data: visibleEquipment
                ?.filter((equip) =>
                  equip.assignedToLocations?.includes(location._id)
                )
                ?.sort(
                  (a, b) =>
                    ((visibleEquipment?.length || 0) *
                      colorGroupsMap[location._id]?.[a.color]?.orderNumber +
                      equipmentOrderMap[location._id]?.[a._id]?.orderInColorGroup) -
                    ((visibleEquipment?.length || 0) *
                      colorGroupsMap[location._id]?.[b.color]?.orderNumber +
                      equipmentOrderMap[location._id]?.[b._id]?.orderInColorGroup)
                ),
              unavailableClass: 'unavailableItem',
              resourceType: 'equipment',
              holidays: scheduleAvailability.holidays,
              timeoffs: scheduleAvailability.calendarEvents,
              type: RESOURCE_TYPE.equipment,
              Template: ({ item }) => (
                <div
                  className={classnames(
                    classes.equipmentBlock,
                    getEquipmentInfo(item._id, 'className'),
                    item.isUsed && classes.usedResource,
                    canDragResources && classes.cursorPointer
                  )}
                >
                  <div
                    className={classes.equipmentNumber}
                    style={{ backgroundColor: getEquipmentInfo(item._id, 'color') }}
                  >
                    {getEquipmentInfo(item._id, 'number')}
                  </div>
                  <div
                    className={classes.equipmentName}
                    style={{ color: getEquipmentInfo(item._id, 'color') }}
                  >
                    {getEquipmentInfo(item._id, 'name')}
                  </div>
                </div>
              ),
            },
          ],
        })),
    [
      visibleEquipment,
      visibleUsers,
      classes,
      getEquipmentInfo,
      scheduleAvailability,
      locations,
      showAllLocations,
      currentUser,
      visibleLocations,
      canDragResources,
    ]
  );

  return (
    <div
      ref={containerRef}
      className={classnames(
        isAdminMobile ? classes.flexColum : classes.resourcesWrapper
      )}
    >
      <div className={classes.flexRow}>
        <div className={classes.column}>
          <Typography
            variant="body1"
            color="textPrimary"
            className={classes.marginBottomBig}
          >
            People
          </Typography>
        </div>
        <div className={classes.column}>
          <Typography
            variant="body1"
            color="textPrimary"
            className={classes.marginBottomBig}
          >
            Equipment
          </Typography>
        </div>
      </div>

      {locationsResources.map(
        ({ location, resources, onLocationClick, isLocationVisible }) => (
          <div key={location._id} className={classes.flexColum}>
            {showAllLocations && (
              <div
                className={classnames(classes.flexRow, classes.locationRow)}
                onClick={onLocationClick}
              >
                <div className={classes.locationName}>
                  <span
                    className={classes.locationColorBox}
                    style={{ backgroundColor: location.color }}
                  >
                    {' '}
                  </span>
                  {location.name}
                </div>
                <div className={classes.expandButton}>
                  {isLocationVisible ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </div>
              </div>
            )}
            {isLocationVisible && (
              <div className={classes.flexRow}>
                {resources.map(
                  ({
                    title,
                    resourceType,
                    data,
                    unavailableClass,
                    Template,
                    timeoffs,
                    type,
                    holidays,
                  }) => (
                    <div key={title} className={classes.column}>
                      {dataReady && (
                        <>
                          {!data.length && (
                            <Typography variant="body1" color="textPrimary">
                              No Results
                            </Typography>
                          )}
                          {!!data.length &&
                            data.map((item) => (
                              <div
                                key={item._id}
                                className={classes.resourceWrapper}
                              >
                                <WarningToolTip
                                  classes={classes}
                                  resource={item}
                                  timeoffs={timeoffs}
                                  scheduleDate={date}
                                  dateFormatWithTime={dateFormatWithTime}
                                  dateFormat={dateFormat}
                                  containerRef={containerRef}
                                  type={type}
                                  holidays={holidays}
                                />
                                <div
                                  className={classnames(
                                    classes.marginBottom,
                                    classes.itemWrapper
                                  )}
                                  draggable={canDragResources}
                                  onDragStart={setDragElementData(
                                    item._id,
                                    resourceType
                                  )}
                                >
                                  <Template item={item} />
                                </div>
                              </div>
                            ))}
                        </>
                      )}
                    </div>
                  )
                )}
              </div>
            )}
          </div>
        )
      )}
    </div>
  );
};

ResourcesList.propTypes = {
  searchValue: PropTypes.string.isRequired,
  isAdminMobile: PropTypes.bool,
};

export default ResourcesList;

